import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IonAlert, IonCheckbox, IonItem, IonLabel, IonLoading, IonList, isPlatform} from '@ionic/react';
import Modal from '@components/modal/modal.component';
import {StyledButton, StyledRedButton} from '@components/button/button.style';
import {StyledModalContent} from '@components/modal/modal.style';
import {StyledFooter} from '@components/pane/pane.style';
import {
    getPendingDocuments,
    acceptDocuments,
    rejectDocuments,
    getDocumentBase64,
    SalaryTransparencyDocument,
} from '@services/salaryTransparency.service';
import {base64ToBlob} from '@services/file.service';
import {Filesystem, Directory} from '@capacitor/filesystem';
import {FileOpener} from '@capacitor-community/file-opener';
import {Browser} from '@capacitor/browser';

type Props = {
    isOpen: boolean;
    trigger: 'matching' | 'admin_match';
    onAccepted: () => void;
    onRejectedUnmatch: () => void;
    onRejectedLogout: () => void;
};

const SalaryTransparencyModal: React.FC<Props> = ({
    isOpen,
    trigger,
    onAccepted,
    onRejectedUnmatch,
    onRejectedLogout,
}) => {
    const {t} = useTranslation();
    const [documents, setDocuments] = useState<SalaryTransparencyDocument[]>([]);
    const [activeContractId, setActiveContractId] = useState<number | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showRejectAlert, setShowRejectAlert] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setConfirmed(false);
            loadDocuments();
        }
    }, [isOpen]);

    const loadDocuments = async () => {
        setIsLoading(true);
        try {
            const result = await getPendingDocuments();
            setDocuments(result.documents);
            setActiveContractId(result.activeContractId);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const openDocument = async (doc: SalaryTransparencyDocument) => {
        if (!doc.mime_type) return;
        try {
            setIsLoading(true);
            const base64Data = await getDocumentBase64(doc);
            const fileName = doc.original_name ?? doc.name + '.pdf';

            if (isPlatform('capacitor')) {
                const result = await Filesystem.writeFile({
                    path: 'ExactPeople/salary/' + fileName,
                    data: base64Data,
                    directory: Directory.Library,
                    recursive: true,
                });
                await FileOpener.open({
                    filePath: result.uri,
                    contentType: doc.mime_type,
                    openWithDefault: false,
                });
            } else {
                const blob = base64ToBlob(base64Data, doc.mime_type);
                const url = URL.createObjectURL(blob);
                await Browser.open({url});
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async () => {
        setIsLoading(true);
        try {
            const systemIds = documents.filter(d => d.type === 'system_document').map(d => d.id);
            const clientIds = documents.filter(d => d.type === 'client_document').map(d => d.id);
            await acceptDocuments(systemIds, clientIds);
            onAccepted();
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectConfirm = async () => {
        setIsLoading(true);
        try {
            const result = await rejectDocuments(trigger);
            if (result.action === 'unmatched') {
                onRejectedUnmatch();
            } else {
                onRejectedLogout();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const rejectMessage = trigger === 'matching'
        ? t('salaryTransparency.rejectConfirm.matching')
        : t('salaryTransparency.rejectConfirm.adminMatch');

    return (
        <>
            <IonLoading isOpen={isLoading}/>
            <Modal
                isOpen={isOpen}
                backdropDismiss={false}
                disableClose={true}
                breakpoints={[0.99]}
                initialBreakpoint={0.99}
                onDidDismiss={() => {}}
            >
                <StyledModalContent>
                    <div className="title">
                        <h2>{t('salaryTransparency.title')}</h2>
                        <p>{t('salaryTransparency.description')}</p>
                    </div>
                    <div className="content">
                        <IonList>
                            {documents.map(doc => (
                                <IonItem
                                    key={doc.id}
                                    button
                                    onClick={() => openDocument(doc)}
                                >
                                    <IonLabel>{doc.name}</IonLabel>
                                </IonItem>
                            ))}
                        </IonList>

                        <IonItem lines="none" style={{marginTop: 16, marginBottom: 100}}>
                            <IonCheckbox
                                slot="start"
                                checked={confirmed}
                                onIonChange={e => setConfirmed(e.detail.checked)}
                            />
                            <IonLabel style={{whiteSpace: 'normal'}}>
                                {t('salaryTransparency.confirmCheckbox')}
                            </IonLabel>
                        </IonItem>
                    </div>
                </StyledModalContent>

                <StyledFooter style={{justifyContent: 'space-between'}}>
                    <StyledRedButton
                        onClick={() => setShowRejectAlert(true)}
                        disabled={isLoading}
                    >
                        {t('salaryTransparency.reject')}
                    </StyledRedButton>
                    <StyledButton
                        onClick={handleAccept}
                        disabled={!confirmed || isLoading}
                    >
                        {t('salaryTransparency.accept')}
                    </StyledButton>
                </StyledFooter>
            </Modal>

            <IonAlert
                isOpen={showRejectAlert}
                onDidDismiss={() => setShowRejectAlert(false)}
                message={rejectMessage}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        handler: () => setShowRejectAlert(false),
                    },
                    {
                        text: t('common.alertConfirm'),
                        handler: handleRejectConfirm,
                    },
                ]}
            />
        </>
    );
};

export default SalaryTransparencyModal;
